/*
 * Pure rules behind the warm-transfer picker and status line (PROD-10258).
 */

import type {
  TransferChannel,
  TransferStatus,
  TransferTarget,
  TransferTargetStatus
} from './transfer_api';

export const TRANSFER_NOTE_MAX = 140;

export const TARGET_STATUS_LABEL: Record<TransferTargetStatus, string> = {
  available: 'Available',
  phone: 'Rings phone',
  on_call: 'On a call',
  offline: 'Offline'
};

export const isTargetSelectable = (target: TransferTarget): boolean =>
  target.status === 'available' || target.status === 'phone';

/** A's status line while B rings: names the phone so A knows B may take a moment to answer. */
export const transferRingingLabel = (toName: string, channel?: TransferChannel): string => {
  const who = toName || 'your colleague';
  return channel === 'phone' ? `Ringing ${who}'s phone` : `Ringing ${who}`;
};

export const filterTargets = (targets: TransferTarget[], query: string): TransferTarget[] => {
  const q = query.trim().toLowerCase();
  if (!q) return targets;
  return targets.filter((t) => `${t.name} ${t.email}`.toLowerCase().includes(q));
};

/** Whole seconds until a unix-seconds deadline, never negative. */
export const secondsLeft = (expiresAt: number, nowMs: number): number =>
  Math.max(0, Math.ceil(expiresAt - nowMs / 1000));

export const isFinalTransferStatus = (status: TransferStatus): boolean =>
  status !== 'ringing' && status !== 'accepted';

/** Toast for A when a transfer ends without B; null when there is nothing to say. */
export const transferOutcomeMessage = (status: TransferStatus, toName: string): string | null => {
  const who = toName || 'Your colleague';
  switch (status) {
    case 'declined':
      return `${who} declined the transfer. You are still on the call.`;
    case 'timed_out':
      return `${who} did not answer. You are still on the call.`;
    case 'failed':
      return `Could not reach ${toName || 'your colleague'}. You are still on the call.`;
    case 'returned':
      return `${who} left. The call is back with you.`;
    default:
      return null;
  }
};

export interface TransferInvite {
  transferUUID: string;
  fromAgentName: string;
  note: string;
  expiresAt: number;
}

/** The pill's view of a transfer ring, from the transfer_request relay or GET /transfer. */
export const transferInviteFrom = (data: {
  transfer_uuid?: string;
  from_agent_name?: string;
  note?: string;
  expires_at?: number;
}): TransferInvite => ({
  transferUUID: data.transfer_uuid || '',
  fromAgentName: data.from_agent_name || '',
  note: data.note || '',
  expiresAt: data.expires_at || 0
});

export const transferRingBody = (fromAgentName?: string, caller?: string): string =>
  `Transfer from ${fromAgentName || 'a colleague'}: ${caller || 'a caller'}`;

/** A owns the Transfer button; B (transfer) and joiners never do. B is in the room from the moment it answers. */
export const incomingPillRoles = (args: {
  isJoiner: boolean;
  transfer: TransferInvite | null | undefined;
  unlocked: boolean;
  status: string;
}): { isOriginator: boolean; inRoom: boolean } => ({
  isOriginator: !args.isJoiner && !args.transfer,
  inRoom: args.unlocked || (!!args.transfer && args.status !== 'ringing')
});

/** A routed ring carries ParentCallSid; a bare REST dial with no relay yet may be a transfer leg. */
export const mayBeTransferLeg = (args: {
  hasMeta: boolean;
  parentSid?: string | null;
  isJoiner: boolean;
  childSid?: string | null;
}): boolean => !args.hasMeta && !args.parentSid && !args.isJoiner && !!args.childSid;

/** Agent cell for a call: "A → B" once a transfer reached B, else just A. */
export const agentWithTransfer = (firstAgent: string, transferredTo?: string | null): string =>
  transferredTo ? `${firstAgent} → ${transferredTo}` : firstAgent;

// Matches the pill's own phone breakpoint in outbound_call_pill.css.
export const TRANSFER_SHEET_MAX_WIDTH = 480;
const PANEL_GAP = 8;
const PANEL_MIN_HEIGHT = 280;

export interface PanelPlacement {
  mode: 'anchored' | 'sheet';
  top?: number;
  right?: number;
  maxHeight: number;
}

/** Hangs the picker under the call pill on desktop; on phones it is a bottom sheet that stops short of the pill. */
export const transferPanelPlacement = (
  pill: { bottom: number; right: number } | null,
  viewport: { width: number; height: number }
): PanelPlacement => {
  const below = pill ? pill.bottom + PANEL_GAP : 150;
  const maxHeight = Math.max(PANEL_MIN_HEIGHT, viewport.height - below - PANEL_GAP * 2);
  if (viewport.width <= TRANSFER_SHEET_MAX_WIDTH) return { mode: 'sheet', maxHeight };
  return {
    mode: 'anchored',
    top: below,
    right: pill ? Math.max(PANEL_GAP, viewport.width - pill.right) : 20,
    maxHeight
  };
};

/** "Transfer to Dovin": the button names who will ring, so the pick is never in doubt. */
export const transferButtonLabel = (selectedName: string | null | undefined): string => {
  const first = (selectedName || '').trim().split(/\s+/)[0];
  return first ? `Transfer to ${first}` : 'Transfer';
};

interface IncomingInviteLike {
  callSid?: string | null;
  ringSids?: Array<string | null | undefined>;
  callUUID?: string | null;
  fromNumber?: string | null;
  studentName?: string | null;
  studentUuid?: string | null;
  transfer?: TransferInvite | null;
}

/** True when an open, not-yet-explained invite is the ring leg this transfer data names. */
export const inviteAwaitsTransfer = (
  active: IncomingInviteLike | null | undefined,
  sid: string | null | undefined
): boolean =>
  !!active &&
  !!sid &&
  !active.transfer &&
  (active.callSid === sid || !!active.ringSids?.includes(sid));

/** The open invite with the transfer's ring details, from the transfer_request relay or GET /transfer. */
export const withTransferInvite = <T extends IncomingInviteLike>(
  active: T,
  data: {
    call_uuid?: string;
    from_number?: string;
    student_name?: string;
    student_uuid?: string;
    transfer_uuid?: string;
    from_agent_name?: string;
    note?: string;
    expires_at?: number;
  }
): T => ({
  ...active,
  callUUID: data.call_uuid || active.callUUID || null,
  fromNumber: data.from_number || active.fromNumber || null,
  studentName: data.student_name || active.studentName || null,
  studentUuid: data.student_uuid || active.studentUuid || null,
  transfer: transferInviteFrom(data)
});

/** Still ringing B; any other status means B's ring must stop. */
export const isTransferRinging = (status: string | null | undefined): boolean =>
  status === 'ringing';
